import {useInvertedSurface} from '@hconnect/common/styles'
import {customThemeConstants, TextOnBackground} from '@hconnect/uikit/src/lib2'
import {
  Stepper,
  Step as MuiStep,
  StepLabel as MuiStepLabel,
  stepLabelClasses,
  StepIcon as MuiStepIcon,
  stepIconClasses,
  StepConnector as MuiStepConnector,
  stepConnectorClasses,
  styled,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material'

import {useWidgetTranslation} from '../hooks'
import {type Step} from '../types'

type Props = {
  activeStep: number
  steps: Step[]
}

const StepConnector = styled(MuiStepConnector)({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 20,
    borderColor: customThemeConstants().palette.mediumInputBorder,
    left: 'calc(-50% + 28px)',
    right: 'calc(50% + 28px)'
  }
})

const StepLabel = styled(MuiStepLabel, {
  shouldForwardProp: (prop) => prop !== 'inverted'
})<{inverted: boolean}>(({theme, inverted}) => ({
  [`& .${stepLabelClasses.labelContainer} .${stepLabelClasses.alternativeLabel}`]: {
    color: inverted ? theme.palette.common.white : theme.palette.text.primary
  },
  ...(!inverted && {
    [`& .${stepIconClasses.root}`]: {
      borderColor: theme.palette.divider
    },
    [`& .${stepIconClasses.root}:not(.${stepIconClasses.active})`]: {
      color: theme.palette.background.paper
    },
    [`& .${stepIconClasses.root}:not(.${stepIconClasses.active}) .${stepIconClasses.text}`]: {
      fill: theme.palette.text.secondary
    }
  })
}))

const StepIcon = styled(MuiStepIcon)({
  width: 40,
  height: 40,
  border: '1px solid',
  borderRadius: '50%',
  borderColor: customThemeConstants().palette.mediumInputBorder,
  color: customThemeConstants().palette.primaryBackgroundColor
})

const CompactHorizontalStepper = ({
  activeLabel,
  activeStep,
  totalSteps,
  textColor
}: {
  activeLabel: string
  activeStep: number
  totalSteps: number
  textColor: string
}) => {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const {t} = useWidgetTranslation()

  return (
    <Stack direction="row" spacing={2} justifyContent="space-between" px={2}>
      <TextOnBackground variant={isSmall ? 'h5' : 'h3'} contrast="high" color={textColor}>
        {activeLabel}
      </TextOnBackground>
      <TextOnBackground variant={isSmall ? 'body2' : 'body1'} contrast="low" color={textColor}>
        {t('label.stepsProgress', {activeStep, totalSteps})}
      </TextOnBackground>
    </Stack>
  )
}

export const HorizontalStepper = ({steps, activeStep}: Props) => {
  const theme = useTheme()
  const inverted = useInvertedSurface()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  if (isMobile) {
    return (
      <CompactHorizontalStepper
        activeLabel={steps[activeStep - 1]?.label}
        activeStep={activeStep}
        totalSteps={steps.length}
        textColor={inverted ? 'primary.contrastText' : 'text.primary'}
      />
    )
  }

  return (
    <Stepper activeStep={activeStep} alternativeLabel connector={<StepConnector />}>
      {steps.map(({key, label}, index) => (
        <MuiStep key={key} active={activeStep === index + 1} completed={false}>
          <StepLabel StepIconComponent={StepIcon} inverted={inverted}>
            {label}
          </StepLabel>
        </MuiStep>
      ))}
    </Stepper>
  )
}
